import { uid } from '../../../../lib/uid';
import { SALUTATIONS } from '../../../../lib/salutations';
import type { IdentificationType } from '../../identification/identificationTypes';
import type {
  Citizenship,
  CivilStatus,
  EmploymentType,
  MilitaryBranch,
  MilitaryServiceStatus,
  PersonnelCatalogs,
  Sex,
} from '../personnelTypes';

/**
 * Test data backing the Autogenerate button on the personnel form.
 *
 * Fills every required field so the primary action enables on the first try — **and the
 * optional ones the form can offer a real value for**, because "Save enables" is the floor
 * rather than the whole contract. A record generated with only its required fields produces a
 * profile that is a column of em dashes, which exercises none of the screens behind the form
 * and is indistinguishable from the bug where a section was never wired.
 *
 * Keep it in lockstep with the form: a new required field must be added here in the same
 * change, or Autogenerate silently stops working.
 *
 * **The names here are deliberately not names.** Every other generator in the product picks
 * from plausible vocabulary, and this one must not: a generated record writes a row into
 * `rsys_pii`, the one store whose entire purpose is holding real people's identities. A
 * fabricated-but-realistic name sitting in that table is indistinguishable from a real one to
 * anyone auditing it later, and "is this row a person?" is the question that store exists to
 * make answerable. So the values read as obviously synthetic on sight.
 */

const GIVEN = ['Sample', 'Probe', 'Fixture', 'Placeholder', 'Draft', 'Example'];
const FAMILY = ['Testcase', 'Testrow', 'Testentry', 'Testrecord'];

/** A real row from a catalog the form already loaded, or null when it holds none. */
function pick<T>(rows: readonly T[]): T | null {
  return rows.length === 0 ? null : rows[Math.floor(Math.random() * rows.length)];
}

export interface SamplePersonnel {
  salutation: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  dateOfBirth: string;
  dateHired: string;

  /*
    The first line of service's own dates. The instrument is dated BEFORE the assumption,
    which is the one ordering the server refuses to have backwards.
  */
  appointmentDate: string;
  assumptionDate: string;

  /** A real row out of the catalog, or null when it holds none. */
  appointmentNatureId: string | null;
  sex: Sex;
  civilStatus: CivilStatus;
  citizenship: Citizenship;
  employmentType: EmploymentType;
  contactNumber: string;
  email: string;

  /**
   * Filled even though `isPwd` stays false, and the military block likewise.
   *
   * Both sit behind a control the reader owns — a checkbox that reveals a field, a flag that
   * adds a whole step — and flipping one would make the same button produce a different form
   * on each click. Filling what is BEHIND them costs nothing and is invisible until the reader
   * ticks the box, at which point the section is already populated and gets exercised in one
   * click rather than by typing seven more fields.
   */
  pwdIdNumber: string;
  militaryBranch: MilitaryBranch;
  militaryServiceStatus: MilitaryServiceStatus;
  militaryRank: string;
  militaryServiceNumber: string;
  militaryUnit: string;
  militaryEntered: string;
  militarySeparated: string;

  /** Catalog ids, resolved from what the form loaded — null where the catalog is empty. */
  bloodTypeId: string | null;
  nationalityId: string | null;
  religionId: string | null;
  hmoProviderId: string | null;
  /** The picker binds to the NAME when the reader may add providers, so both travel. */
  hmoProviderName: string;
  hmoCardNumber: string;

  /** One number per identification type the catalog holds, keyed by type id. */
  identifications: Record<string, string>;

  /**
   * A present and a permanent address, different from each other so the "same as present"
   * flag has something to be false about.
   *
   * Obviously synthetic, like the names: an address is not subject PII the way a name is, but
   * a plausible one on a generated record is still a street somebody lives on.
   */
  presentAddress: SampleAddress;
  permanentAddress: SampleAddress;
}

export interface SampleAddress {
  streetAddress: string;
  barangay: string;
  cityMunicipality: string;
  province: string;
  zipCode: string;
}

/**
 * One employee.
 *
 * **The catalogs are an argument, and that is what makes the foreign keys real.** A generator
 * with no parameters cannot pick a row that exists, so it can only leave every catalog-backed
 * field empty — which is exactly what this one used to do. An invented id would be worse: the
 * server refuses one that is not active, and the failure reads as a bug in the form.
 *
 * **Four things are left empty on purpose, and each for a different reason:**
 *
 * - **No appointment.** The Employment step offers a vacant position, and filling it would
 *   write to the *plantilla* — another module's authoritative staffing record — consuming a
 *   real vacancy every time the button is clicked. A convenience button does not get to do
 *   that, which is the same line the account draws.
 * - **No account.** Minting a real sign-in with a real password as a side effect of a
 *   convenience button is not a thing this should do, and skipping is already valid.
 * - **No photograph.** The server verifies the format by magic bytes and the image lands in
 *   `rsys_pii` as encrypted parts; a generated one is more machinery than a fixture warrants.
 * - **No access level.** Classifying the sample would hide it from the reader who generated
 *   it, which is the one outcome a convenience button must not produce. It is the call
 *   `isMilitary` makes below — a field that changes what is on screen stays at its default —
 *   one step worse, because here the default is also the only safe answer.
 *
 * `isMilitary` and `isPwd` stay at their defaults for a fifth reason — a flag that adds a
 * step or reveals a field would make the same button produce a different form on each click —
 * but everything *behind* them is filled, so one tick exercises the whole section.
 *
 * The employee number is server-assigned and is not ours to invent.
 */
export function samplePersonnel({
  catalogs,
  identificationTypes,
}: {
  catalogs: PersonnelCatalogs;
  identificationTypes: readonly IdentificationType[];
}): SamplePersonnel {
  const tag = uid().slice(0, 6).toLowerCase();
  const given = GIVEN[Math.floor(Math.random() * GIVEN.length)];
  const family = FAMILY[Math.floor(Math.random() * FAMILY.length)];

  /*
    A plausible working age, derived rather than fixed so repeated records are not identical.
    Built from parts and formatted by hand: `toISOString` would convert through UTC and could
    hand the input a different day than the one intended — a date of birth is a calendar date,
    not an instant.
  */
  const year = 1970 + Math.floor(Math.random() * 30);
  const month = 1 + Math.floor(Math.random() * 12);
  const day = 1 + Math.floor(Math.random() * 28);
  const pad = (n: number) => String(n).padStart(2, '0');

  const hmo = pick(catalogs.hmoProviders);

  /*
    Service dates that satisfy the form's own rules without depending on today: entry at 20,
    separation eight years later, both comfortably in the past given a birth year of at most
    1999. The server refuses a separation before the entry, so the order is derived rather
    than drawn twice.
  */
  const entered = year + 20;
  const separated = entered + 8;

  /*
    The hire date, built once because two other dates are derived FROM it. Drawing them
    separately is how a generator produces an assumption before its own appointment — the one
    ordering the server refuses to have backwards.
  */
  const hired = `${year + 20 + Math.floor(Math.random() * 5)}-${pad(month)}-${pad(day)}`;

  return {
    // Optional, and filled anyway: "the primary action enables" is the FLOOR, and a
    // record of only required fields exercises nothing.
    salutation: SALUTATIONS[Math.floor(Math.random() * SALUTATIONS.length)],
    firstName: given,
    middleName: family,
    lastName: `${family} ${tag}`,

    // Exercises the joined Last name + Suffix control rather than leaving its narrow segment
    // permanently blank. A generational suffix identifies nobody on its own.
    suffix: 'Jr.',
    dateOfBirth: `${year}-${pad(month)}-${pad(day)}`,

    /*
      A hire date the form will accept: required, in the past, and comfortably after the
      date of birth above. Built from parts for the same reason — `toISOString` converts
      through UTC and can hand the field a different day than the one intended.
    */
    dateHired: hired,

    /*
      The instrument a fortnight before the assumption, and the assumption on the hire date —
      the ordinary shape, and one the server accepts. A generator that produced a rejection
      would be worse than one that filled less.
    */
    appointmentDate: shiftDays(hired, -14),
    assumptionDate: hired,

    // A REAL row out of the catalog the form already loaded. An id the generator invented
    // fails the server's "must exist and be active" check, which reads as a bug in the form.
    appointmentNatureId: pick(catalogs.appointmentNatures)?.id ?? null,
    sex: (1 + Math.floor(Math.random() * 2)) as Sex,
    civilStatus: (1 + Math.floor(Math.random() * 6)) as CivilStatus,

    /*
      Filipino, always — NOT a random one of the two. Dual citizenship reveals two further
      required fields, and a generator that sometimes produces them is one whose output the
      reader cannot predict: the same rule that keeps the military flag at its default. The
      dual branch is exercised by picking it, which is one click.
    */
    citizenship: 1 as Citizenship,
    employmentType: (1 + Math.floor(Math.random() * 6)) as EmploymentType,

    // Not a real range, and never a real number: 555 is the reserved fiction block.
    contactNumber: `+63 917 555 ${String(Math.floor(Math.random() * 9000) + 1000)}`,

    // example.test can never resolve, so a generated record cannot mail anyone.
    email: `${given.toLowerCase()}.${tag}@example.test`,

    // Behind the PWD checkbox and the military flag — see the note on the type above.
    pwdIdNumber: `PWD-${tag.toUpperCase()}`,
    militaryBranch: (1 + Math.floor(Math.random() * 5)) as MilitaryBranch,
    militaryServiceStatus: (1 + Math.floor(Math.random() * 4)) as MilitaryServiceStatus,
    militaryRank: 'Sample Rank',
    militaryServiceNumber: `SVC-${tag.toUpperCase()}`,
    militaryUnit: 'Sample Unit',
    militaryEntered: `${entered}-${pad(month)}-${pad(day)}`,
    militarySeparated: `${separated}-${pad(month)}-${pad(day)}`,

    // Real rows from the loaded catalogs. Null where a catalog is empty — which is a module
    // with nothing configured, not a value to invent.
    bloodTypeId: pick(catalogs.bloodTypes)?.id ?? null,
    nationalityId: pick(catalogs.nationalities)?.id ?? null,
    religionId: pick(catalogs.religions)?.id ?? null,
    hmoProviderId: hmo?.id ?? null,
    hmoProviderName: hmo?.name ?? '',
    hmoCardNumber: hmo ? `HMO-${tag.toUpperCase()}` : '',

    /*
      One number per type the catalog actually holds, so the Identification panel has something
      to show. The numbers are synthetic on sight for the same reason the names are — a
      statutory number that looks real is one somebody will eventually treat as real.
    */
    presentAddress: {
      streetAddress: `${100 + Math.floor(Math.random() * 800)} Sample Street`,
      barangay: 'Barangay Testcase',
      cityMunicipality: 'Test City',
      province: 'Test Province',
      zipCode: String(1000 + Math.floor(Math.random() * 8999)),
    },

    permanentAddress: {
      streetAddress: `${100 + Math.floor(Math.random() * 800)} Placeholder Road`,
      barangay: 'Barangay Testrow',
      cityMunicipality: 'Sample Municipality',
      province: 'Sample Province',
      zipCode: String(1000 + Math.floor(Math.random() * 8999)),
    },

    identifications: Object.fromEntries(
      identificationTypes.map((type) => [
        type.id,
        `${(type.code ?? type.name).replace(/\s+/g, '').toUpperCase().slice(0, 6)}-${tag.toUpperCase()}`,
      ]),
    ),
  };
}

/**
 * Every vocabulary, empty — for a caller whose form owns no catalog-backed picker.
 *
 * **One constant, because there were seven copies of this literal** and adding a sixth
 * vocabulary meant editing all seven. `samplePersonnel` resolves foreign keys against the
 * catalogs the form already loaded, so a section editor that draws none still has to pass the
 * shape; it should not have to restate it.
 *
 * `pick` returns null on an empty list, so a generator handed this simply fills no
 * catalog-backed field — which is the honest outcome for a form that has no such field.
 */
export const NO_CATALOGS: PersonnelCatalogs = {
  bloodTypes: [],
  nationalities: [],
  religions: [],
  hmoProviders: [],
  countries: [],
  appointmentNatures: [],
  specialSkillCategories: [],
};

/**
 * A calendar date shifted by whole days — never `Date` arithmetic on a string.
 *
 * Built from local parts and read back from local parts, because `toISOString` converts through
 * UTC and can hand back a different day than the one intended. Every date on the personnel form
 * is a day rather than an instant.
 */
function shiftDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);

  // Noon, never midnight: in a zone whose DST transition lands at midnight that hour does not
  // exist and the browser settles on the day either side.
  const shifted = new Date(year, month - 1, day, 12);

  shifted.setDate(shifted.getDate() + days);

  const m = String(shifted.getMonth() + 1).padStart(2, '0');
  const d = String(shifted.getDate()).padStart(2, '0');

  return `${shifted.getFullYear()}-${m}-${d}`;
}
